function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const urlParams = new URLSearchParams(window.location.search);
    const hasFilters = [...urlParams.keys()].some(key => key.startsWith('filter.v.'));
    const hasSort = urlParams.has('sort_by') && urlParams.get('sort_by') !== '';
    const facetRemove = document.querySelector('facet-remove');
    if (facetRemove && (hasFilters || hasSort)) {
      facetRemove.style.display = 'block';
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const loadingSpinners = document.querySelectorAll('.products_inner_wrapper .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer && productGridContainer.querySelector('.collection')) {
      productGridContainer.querySelector('.collection').classList.add('loading');
    }

    if (countContainer) countContainer.classList.add('loading');

    // ✅ Fix: Ensure `q` and `type` are preserved
    const currentParams = new URLSearchParams(searchParams);
    if (!currentParams.has('q')) {
      const q = new URLSearchParams(window.location.search).get('q');
      if (q) currentParams.set('q', q);
    }
    
    const url = `${window.location.pathname}?${currentParams.toString()}`;

    const filterDataUrl = (element) => element.url === url;

    FacetFiltersForm.filterData.some(filterDataUrl)
      ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
      : FacetFiltersForm.renderSectionFromFetch(url, event);

    if (updateURLHash) FacetFiltersForm.updateURLHash(currentParams.toString());
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(html.innerHTML);
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(html.innerHTML);
    }
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsedHTML.getElementById('ProductGridContainer');

    if (newGrid) {
      document.getElementById('ProductGridContainer').innerHTML = newGrid.innerHTML;
    } else {
      console.warn('ProductGridContainer not found in response HTML');
    }
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (countEl && container) {
      container.innerHTML = countEl.innerHTML;
      container.classList.remove('loading');
    }

    if (countEl && containerDesktop) {
      containerDesktop.innerHTML = countEl.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll('.products_inner_wrapper .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElements = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter');

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };

    const facetsToRender = Array.from(facetDetailsElements).filter((el) => !matchesIndex(el));
    const facetRemove = document.querySelector('facet-remove');

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) {
        target.innerHTML = element.innerHTML;
      }
    });

    const checkedCount = document.querySelectorAll('#FacetFiltersForm input[type="checkbox"]:checked').length;
    const minPriceInput = document.querySelector('#FacetFiltersForm input[name*="filter.v.price.gte"]');
    const maxPriceInput = document.querySelector('#FacetFiltersForm input[name*="filter.v.price.lte"]');

    const priceMin = minPriceInput?.value.trim() || '';
    const priceMax = maxPriceInput?.value.trim() || '';

    const isPriceActive = priceMin !== '' || priceMax !== '';
    const shouldShowClear = checkedCount > 0 || isPriceActive;


    if (facetRemove) {
      facetRemove.style.display =shouldShowClear ? 'block' : 'none';

      const clearAll = parsedHTML.querySelector('a.clear-all-button');
      if (clearAll) {
        facetRemove.innerHTML = '';
        facetRemove.appendChild(clearAll.cloneNode(true));
      }
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
  }

  static renderActiveFacets(html) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach((selector) => {
      const newEl = html.querySelector(selector);
      if (!newEl) return;
      const current = document.querySelector(selector);
      if (current) current.innerHTML = newEl.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}?${searchParams}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid-card')?.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className === 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetSortMobForm';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortDrawerForm' || form.id === 'FacetFiltersForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetSortMobForm' || form.id === 'FacetFiltersForm') {
          forms.push(this.createSearchParams(form));
        }
      });

      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.includes('?')
      ? event.currentTarget.href.split('?')[1]
      : '';
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.rangeMin = this.querySelector('.range-min');
    this.rangeMax = this.querySelector('.range-max');
    this.inputMin = this.querySelector('.input-min');
    this.inputMax = this.querySelector('.input-max');

    // Update input values when range sliders are moved
    this.rangeMin.addEventListener('input', () => {
      this.inputMin.value = parseInt(this.rangeMin.value, 10);
    });

    this.rangeMax.addEventListener('input', () => {
      this.inputMax.value = parseInt(this.rangeMax.value, 10);
    });

    // Update range slider when input fields are changed
    this.inputMin.addEventListener('input', () => {
      const value = parseInt(this.inputMin.value, 10);
      if (value <= parseInt(this.rangeMax.value, 10)) {
        this.rangeMin.value = value;
         updateRange();
      }
    });

    this.inputMax.addEventListener('input', () => {
      const value = parseInt(this.inputMax.value, 10);
      if (value >= parseInt(this.rangeMin.value, 10)) {
        this.rangeMax.value = value;
         updateRange();
      }
    });
  }
}

customElements.define('price-range', PriceRange);



class FacetRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const facetLink = this.querySelector('a');
    if (facetLink) {
      facetLink.setAttribute('role', 'button');
      facetLink.addEventListener('click', this.closeFilter.bind(this));
      facetLink.addEventListener('keyup', (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
      });
    } else {
      console.warn("Facet link not found!");
    }
  }

  closeFilter(event) {
    event.preventDefault();
    $(".faq_header").removeClass("active");
    $(".faq_content").slideUp();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);






